import { useState, useRef, useCallback, useEffect } from "react";
import {
  Check,
  ArrowUpLeft,
  Plus,
  X,
  Send,
  Quote as QuoteIcon,
  Star,
  ArrowLeft,
  Calendar,
  Newspaper,
} from "lucide-react";
import {
  getStoreDefaultContent,
  getStoreDefaultMeta,
  StoreSectionBlockRenderer,
  isStoreSectionType,
} from "./StoreSectionBlocks";
import { useSectionDropSlot } from "./SectionDropSlotContext";
import { EditableImage } from "./MediaPickerModal";
import { handleFormatKeyDown, extractRichValue, hasHtmlFormatting } from "./InlineFormatToolbar";
import { BlockFormatBar } from "./BlockFormatBar";
import type { TypoData } from "./BlockFormatBar";

/* ────────────────────────────────────────────────
   Section Block Types — large composite blocks
   that represent full page sections
   ──────────────────────────────────────────────── */

import type { ContentBlock } from "./contentBlockTypes";

/* ────── Default Content ────── */

export function getSectionDefaultContent(type: string): string {
  switch (type) {
    case "sectionContactForm":
      return "משפט הנעה לפעולה";
    case "sectionBenefits":
      return "שם החברה";
    case "sectionNumberedCards":
      return "למה לבחור בנו?";
    case "sectionFeatureCards":
      return "למה זה חשוב?";
    case "sectionServiceCards":
      return "תחומי ההתמחות שלנו";
    case "sectionTestimonials":
      return "מה הלקוחות שלנו אומרים";
    case "sectionSplitContent":
      return "מי אנחנו";
    case "sectionLogos":
      return "הם סומכים עלינו";
    case "sectionBlogPosts":
      return "מהבלוג שלנו";
    case "sectionProcessSteps":
      return "איך זה עובד?";
    default:
      return getStoreDefaultContent(type) || "";
  }
}

/* ────── Default Meta ────── */

export function getSectionDefaultMeta(type: string): Record<string, any> | undefined {
  switch (type) {
    case "sectionContactForm":
      return {
        subtitle: "השאירו פרטים ונחזור אליכם בהקדם",
        fields: [
          { label: "שם מלא", type: "text" },
          { label: "טלפון", type: "tel" },
          { label: "אימייל", type: "email" },
        ],
        buttonLabel: "שליחה",
      };
    case "sectionBenefits":
      return {
        items: [
          { text: "יתרון 1" },
          { text: "יתרון 2" },
          { text: "יתרון 3" },
        ],
      };
    case "sectionNumberedCards":
      return {
        items: [
          { title: "כותרת ראשונה", description: "תיאור מפורט של היתרון או התכונה הראשונה. לחצו כאן כדי לערוך את הטקסט." },
          { title: "כותרת שנייה", description: "תיאור מפורט של היתרון או התכונה השנייה. לחצו כאן כדי לערוך את הטקסט." },
          { title: "כותרת שלישית", description: "תיאור מפורט של היתרון או התכונה השלישית. לחצו כאן כדי לערוך את הטקסט." },
          { title: "כותרת רביעית", description: "תיאור מפורט של היתרון או התכונה הרביעית. לחצו כאן כדי לערוך את הטקסט." },
        ],
      };
    case "sectionFeatureCards":
      return {
        items: [
          { title: "תכונה ראשונה", description: "תיאור מפורט של התכונה הראשונה שמסביר ללקוח למה זה חשוב." },
          { title: "תכונה שנייה", description: "תיאור מפורט של התכונה השנייה שמסביר ללקוח למה זה חשוב." },
          { title: "תכונה שלישית", description: "תיאור מפורט של התכונה השלישית שמסביר ללקוח למה זה חשוב." },
          { title: "תכונה רביעית", description: "תיאור מפורט של התכונה הרביעית שמסביר ללקוח למה זה חשוב." },
        ],
      };
    case "sectionServiceCards":
      return {
        items: [
          { title: "שירות ראשון", description: "תיאור קצר של השירות הראשון שאתם מציעים ללקוחות." },
          { title: "שירות שני", description: "תיאור קצר של השירות השני שאתם מציעים ללקוחות." },
          { title: "שירות שלישי", description: "תיאור קצר של השירות השלישי שאתם מציעים ללקוחות." },
          { title: "שירות רביעי", description: "תיאור קצר של השירות הרביעי שאתם מציעים ללקוחות." },
        ],
      };
    case "sectionTestimonials":
      return {
        items: [
          { name: "ישראל ישראלי", role: "מנכ\"ל, חברה לדוגמה", quote: "שירות מקצועי ואמין. התוצאות מדברות בעד עצמן והצוות היה זמין לכל שאלה.", rating: 5 },
          { name: "שרה כהן", role: "סמנכ\"לית שיווק", quote: "עבודה יסודית עם תשומת לב לפרטים. ממליצה בחום לכל מי שמחפש שירות איכותי.", rating: 5 },
          { name: "דוד לוי", role: "בעל עסק", quote: "חוויה מצוינת מהראשון. מקצועיות, אמינות ותוצאות מעל ומעבר למצופה.", rating: 5 },
        ],
      };
    case "sectionSplitContent":
      return {
        subtitle: "קצת עלינו",
        description: "אנחנו חברה מובילה בתחום עם ניסיון של שנים רבות. הצוות שלנו מורכב ממומחים מנוסים שמביאים ערך מוסף אמיתי לכל פרויקט.\n\nאנו מאמינים בגישה אישית, מקצועיות ללא פשרות ותוצאות שמדברות בעד עצמן.",
        buttonLabel: "קראו עוד",
        buttonLink: "#",
        imagePosition: "left",
      };
    case "sectionLogos":
      return {
        subtitle: "חברות וארגונים שבחרו לעבוד איתנו",
        items: [
          { name: "חברה א'" },
          { name: "חברה ב'" },
          { name: "חברה ג'" },
          { name: "חברה ד'" },
          { name: "חברה ה'" },
          { name: "חברה ו'" },
        ],
      };
    case "sectionBlogPosts":
      return {
        subtitle: "טיפים, חדשות ותובנות מעולם התעשייה",
        items: [
          { title: "5 טיפים לבחירת ספק מקצועי", excerpt: "כיצד לבחור את הספק הנכון עבור הפרויקט שלכם? הנה חמישה דברים שחשוב לבדוק לפני שמתחילים.", date: "18 בפברואר 2026" },
          { title: "מגמות חדשות בתעשייה ב-2026", excerpt: "סקירה מקיפה של המגמות החמות ביותר בתחום שמשנות את חוקי המשחק השנה.", date: "12 בפברואר 2026" },
          { title: "מדריך מקיף למתחילים", excerpt: "כל מה שצריך לדעת כדי להתחיל — מדריך שלב אחר שלב עם דוגמאות מעשיות.", date: "5 בפברואר 2026" },
        ],
        buttonLabel: "לכל הפוסטים",
        buttonLink: "/blog",
      };
    case "sectionProcessSteps":
      return {
        subtitle: "תהליך העבודה שלנו — פשוט, מובנה ויעיל",
        items: [
          { title: "פגישת היכרות", description: "נפגש כדי להבין את הצרכים, המטרות והחזון שלכם. נתכנן יחד את הפרויקט." },
          { title: "תכנון ואפיון", description: "נגדיר את כל הדרישות, מבנה האתר, המסרים ולוח הזמינו המדויק." },
          { title: "עיצוב ופיתוח", description: "הצוות שלנו יתחיל לעבוד — עיצוב מרהיב ופיתוח טכני ברמה הגבוהה ביותר." },
          { title: "השקה ותמיכה", description: "נעלה לאוויר, נוודא שהכל עובד מצוין ונלווה אתכם גם אחרי ההשקה." },
        ],
      };
    default:
      return getStoreDefaultMeta(type) || undefined;
  }
}

/* ────── Editable inline text helper ────── */

const TAG_BADGE_COLORS_SEC: Record<string, string> = {
  h2: "bg-blue-100 text-blue-700",
  h3: "bg-cyan-100 text-cyan-700",
  p: "bg-gray-100 text-gray-600",
  span: "bg-gray-50 text-gray-500",
  div: "bg-gray-100 text-gray-500",
};

const BLOCK_LEVEL_SEC = new Set(["h2", "h3", "p", "div"]);

function InlineEditable({
  value,
  onSave,
  isEditMode,
  tag: Tag = "p",
  className = "",
  placeholder = "\u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4...",
  inputClassName = "",
  typo,
  onTypoChange,
  onTypoReset,
}: {
  value: string;
  onSave: (val: string) => void;
  isEditMode: boolean;
  tag?: "h2" | "h3" | "p" | "span" | "div";
  className?: string;
  placeholder?: string;
  inputClassName?: string;
  typo?: TypoData;
  onTypoChange?: (t: TypoData) => void;
  onTypoReset?: () => void;
}) {
  const ref = useRef<HTMLElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const isRich = hasHtmlFormatting(value);

  const typoStyle: React.CSSProperties = {
    ...(typo?.fontSize ? { fontSize: typo.fontSize } : {}),
    ...(typo?.color ? { color: typo.color } : {}),
    ...(typo?.fontWeight ? { fontWeight: Number(typo.fontWeight) } : {}),
  };

  useEffect(() => {
    if (!isFocused && ref.current) {
      if (isRich) {
        ref.current.innerHTML = value;
      } else {
        ref.current.textContent = value;
      }
    }
  }, [value, isFocused, isRich]);

  const handleBlur = useCallback(() => {
    if (!ref.current) { setIsFocused(false); return; }
    const newVal = extractRichValue(ref.current);
    setIsFocused(false);
    if (newVal !== value) onSave(newVal);
  }, [value, onSave]);

  const renderContent = (text: string) => {
    if (hasHtmlFormatting(text)) {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    }
    return text;
  };

  if (!isEditMode) {
    return <Tag className={className} style={typoStyle}>{value ? renderContent(value) : <span className="text-gray-300">{placeholder}</span>}</Tag>;
  }

  const isBlockTag = BLOCK_LEVEL_SEC.has(Tag);
  const Wrapper = isBlockTag ? "div" : "span";

  return (
    <Wrapper className={`relative ${isBlockTag ? "block" : "inline-block"} group/editable-sec pt-5`}>
      <div className="absolute top-0 right-0 z-[20] flex items-center gap-2 opacity-80 group-hover/editable-sec:opacity-100 transition-opacity">
        <span
          className={`inline-flex items-center text-xs leading-none px-2.5 py-1 rounded-full font-mono shadow-sm ${TAG_BADGE_COLORS_SEC[Tag] || "bg-gray-100 text-gray-500"}`}
          style={{ fontWeight: 700 }}
        >
          {Tag.toUpperCase()}
        </span>
        <BlockFormatBar
          editableEl={ref.current}
          typo={typo}
          onTypoChange={onTypoChange}
          onTypoReset={onTypoReset}
          showBullets
        />
      </div>
      <Tag
        ref={ref as any}
        className={`${className} outline-none ${
          isFocused
            ? "ring-2 ring-zap ring-offset-2 bg-zap-pale/50 rounded px-1"
            : "hover:ring-2 hover:ring-zap/40 hover:ring-offset-1 rounded cursor-text"
        } ${inputClassName}`}
        style={typoStyle}
        contentEditable
        suppressContentEditableWarning
        onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (handleFormatKeyDown(e)) return;
          if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLElement).blur(); }
          if (e.key === "Escape") { if (ref.current) ref.current.textContent = value; (e.target as HTMLElement).blur(); }
        }}
        dir="rtl"
      />
    </Wrapper>
  );
}

/* ────── Multiline Editable ────── */

function MultilineEditable({
  value,
  onSave,
  isEditMode,
  className = "",
  placeholder = "\u05DC\u05D7\u05E6\u05D5 \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4...",
  typo,
  onTypoChange,
  onTypoReset,
}: {
  value: string;
  onSave: (val: string) => void;
  isEditMode: boolean;
  className?: string;
  placeholder?: string;
  typo?: TypoData;
  onTypoChange?: (t: TypoData) => void;
  onTypoReset?: () => void;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const isRich = hasHtmlFormatting(value);

  const typoStyle: React.CSSProperties = {
    ...(typo?.fontSize ? { fontSize: typo.fontSize } : {}),
    ...(typo?.color ? { color: typo.color } : {}),
    ...(typo?.fontWeight ? { fontWeight: Number(typo.fontWeight) } : {}),
  };

  useEffect(() => {
    if (!isFocused && ref.current) {
      if (isRich) {
        ref.current.innerHTML = value;
      } else {
        ref.current.innerHTML = value.replace(/\n/g, "<br>");
      }
    }
  }, [value, isFocused, isRich]);

  const handleBlur = useCallback(() => {
    if (!ref.current) { setIsFocused(false); return; }
    const newVal = extractRichValue(ref.current);
    setIsFocused(false);
    if (newVal !== value) onSave(newVal);
  }, [value, onSave]);

  const renderContent = (text: string) => {
    if (hasHtmlFormatting(text)) {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    }
    return text.split("\n").map((line, i, arr) => (
      <span key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </span>
    ));
  };

  if (!isEditMode) {
    if (!value) return null;
    return <p className={className} style={typoStyle}>{renderContent(value)}</p>;
  }

  return (
    <div className="relative block group/editable-sec pt-5">
      <div className="absolute top-0 right-0 z-[20] flex items-center gap-2 opacity-80 group-hover/editable-sec:opacity-100 transition-opacity">
        <span
          className="inline-flex items-center text-xs leading-none px-2.5 py-1 rounded-full font-mono shadow-sm bg-gray-100 text-gray-600"
          style={{ fontWeight: 700 }}
        >
          P
        </span>
        <BlockFormatBar
          editableEl={ref.current}
          typo={typo}
          onTypoChange={onTypoChange}
          onTypoReset={onTypoReset}
          showBullets
        />
      </div>
      <p
        ref={ref}
        className={`${className} outline-none ${
          isFocused
            ? "ring-2 ring-zap ring-offset-2 bg-zap-pale/50 rounded px-1"
            : "hover:ring-2 hover:ring-zap/40 hover:ring-offset-1 rounded cursor-text"
        }`}
        style={typoStyle}
        contentEditable
        suppressContentEditableWarning
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (handleFormatKeyDown(e)) return;
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
              const range = sel.getRangeAt(0);
              range.deleteContents();
              const br = document.createElement("br");
              range.insertNode(br);
              const txt = document.createTextNode("\u200B");
              range.setStartAfter(br);
              range.insertNode(txt);
              range.setStartAfter(txt);
              range.setEndAfter(txt);
              sel.removeAllRanges();
              sel.addRange(range);
            }
          }
          if (e.key === "Escape") {
            if (ref.current) ref.current.innerHTML = value.replace(/\n/g, "<br>");
            ref.current?.blur();
          }
        }}
        dir="rtl"
      />
    </div>
  );
}

/* ────── Typography helper for section fields ────── */

function makeFieldTypo(
  meta: Record<string, any> | undefined,
  onUpdateMeta: (m: Record<string, any>) => void,
  fieldKey: string,
): { typo: TypoData; onTypoChange: (t: TypoData) => void; onTypoReset: () => void } {
  const mk = `_typo_${fieldKey}`;
  const raw = meta?.[mk] as TypoData | undefined;
  return {
    typo: raw || {},
    onTypoChange: (t: TypoData) => onUpdateMeta({ [mk]: { ...(raw || {}), ...t } }),
    onTypoReset: () => onUpdateMeta({ [mk]: {} }),
  };
}

/* ═══════════════════════════════════════════════
   SECTION RENDERERS
   ═══════════════════════════════════════════════ */

/* ── 1. Contact Form Section ── */

function SectionContactFormBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const meta = block.meta || {};
  const subtitle = meta.subtitle || "";
  const fields: { label: string; type: string }[] = meta.fields || [];
  const buttonLabel = meta.buttonLabel || "שליחה";

  const updateField = (idx: number, label: string) => {
    const next = fields.map((f, i) => (i === idx ? { ...f, label } : f));
    onUpdateMeta({ fields: next });
  };

  const addField = () => {
    onUpdateMeta({ fields: [...fields, { label: "שדה חדש", type: "text" }] });
  };

  const removeField = (idx: number) => {
    onUpdateMeta({ fields: fields.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-white rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-3xl mx-auto text-center">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy mb-3"
          placeholder="כותרת הסקשן..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        {/* Subtitle */}
        <InlineEditable
          value={subtitle}
          onSave={(val) => onUpdateMeta({ subtitle: val })}
          isEditMode={isEditMode}
          tag="p"
          className="text-gray-600 text-lg mb-8"
          placeholder="תת כותרת..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "subtitle")}
        />
        {dropSlot(`secblk-${block.id}-after-subtitle`)}
        {/* Form */}
        <div className="flex flex-wrap items-center gap-3 justify-center">
          {fields.map((field, i) => (
            <div key={i} className="relative group/field flex-1 min-w-[140px] max-w-[220px]">
              {isEditMode ? (
                <input
                  type="text"
                  value={field.label}
                  onChange={(e) => updateField(i, e.target.value)}
                  className="w-full px-4 py-3 rounded-full border-2 border-gray-200 text-center text-gray-600 text-sm outline-none focus:border-zap transition-colors"
                  dir="rtl"
                />
              ) : (
                <input
                  type={field.type}
                  placeholder={field.label}
                  className="w-full px-4 py-3 rounded-full border-2 border-gray-200 text-center text-gray-600 text-sm outline-none focus:border-zap transition-colors"
                  dir="rtl"
                />
              )}
              {isEditMode && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => removeField(i)}
                  onKeyDown={(e) => e.key === "Enter" && removeField(i)}
                  className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/field:opacity-100 transition-opacity cursor-pointer"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </div>
          ))}
          {/* Send button */}
          <div className="min-w-[120px]">
            {isEditMode ? (
              <input
                type="text"
                value={buttonLabel}
                onChange={(e) => onUpdateMeta({ buttonLabel: e.target.value })}
                className="w-full px-6 py-3 rounded-full bg-gray-600 text-white text-center text-sm outline-none"
                style={{ fontWeight: 600 }}
                dir="rtl"
              />
            ) : (
              <button className="w-full px-6 py-3 rounded-full bg-gray-600 text-white text-sm hover:bg-gray-700 transition-colors" style={{ fontWeight: 600 }}>
                {buttonLabel}
              </button>
            )}
          </div>
          {isEditMode && (
            <button
              onClick={addField}
              className="w-10 h-10 rounded-full border-2 border-dashed border-blue-300 text-blue-400 flex items-center justify-center hover:bg-zap-pale hover:border-zap transition-all shrink-0"
              title="הוסף שדה"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
        {dropSlot(`secblk-${block.id}-after-form`)}
      </div>
    </div>
  );
}

/* ── 2. Benefits Section ── */

function SectionBenefitsBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const items: { text: string }[] = block.meta?.items || [];

  const updateItem = (idx: number, text: string) => {
    const next = items.map((item, i) => (i === idx ? { ...item, text } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({ items: [...items, { text: "יתרון חדש" }] });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-gray-100 rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-4xl mx-auto">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy text-center mb-8"
          placeholder="שם החברה..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        {dropSlot(`secblk-${block.id}-after-title`)}
        {/* Cards 2x2 grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((item, i) => (
            <div key={i} className="relative group/benefit bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center">
                <Check className="w-6 h-6 text-gray-700" strokeWidth={2.5} />
              </div>
              <InlineEditable
                value={item.text}
                onSave={(val) => updateItem(i, val)}
                isEditMode={isEditMode}
                tag="p"
                className="text-zap-navy text-sm"
                placeholder="יתרון..."
                {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_text`)}
              />
              {isEditMode && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => removeItem(i)}
                  onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/benefit:opacity-100 transition-opacity cursor-pointer"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </div>
          ))}
          {isEditMode && (
            <button
              onClick={addItem}
              className="border-2 border-dashed border-blue-200 rounded-xl p-6 flex flex-col items-center justify-center text-blue-400 hover:border-zap hover:text-zap hover:bg-zap-pale/30 transition-all min-h-[120px]"
            >
              <Plus className="w-6 h-6 mb-1" />
              <span className="text-xs" style={{ fontWeight: 500 }}>הוסף יתרון</span>
            </button>
          )}
        </div>
        {dropSlot(`secblk-${block.id}-after-grid`)}
      </div>
    </div>
  );
}

/* ── 3. Numbered Content Cards Section ── */

function SectionNumberedCardsBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const items: { title: string; description: string }[] = block.meta?.items || [];

  const updateItem = (idx: number, field: "title" | "description", val: string) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({
      items: [...items, { title: "כותרת חדשה", description: "תיאור מפורט של התכונה או היתרון. לחצו כאן כדי לערוך." }],
    });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-gray-200 rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-5xl mx-auto">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy text-center mb-8"
          placeholder="כותרת הסקשן..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        {dropSlot(`secblk-${block.id}-after-title`)}
        {/* Cards 2x2 grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((item, i) => (
            <div key={i} className="relative group/ncard bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                {/* Number badge */}
                <div className="w-10 h-10 bg-zap text-white rounded-lg flex items-center justify-center shrink-0 text-lg" style={{ fontWeight: 700 }}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <InlineEditable
                    value={item.title}
                    onSave={(val) => updateItem(i, "title", val)}
                    isEditMode={isEditMode}
                    tag="h3"
                    className="text-zap-navy text-base md:text-lg mb-2"
                    placeholder="כותרת..."
                    {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_title`)}
                  />
                  <MultilineEditable
                    value={item.description}
                    onSave={(val) => updateItem(i, "description", val)}
                    isEditMode={isEditMode}
                    className="text-gray-600 text-sm leading-relaxed"
                    placeholder="תיאור..."
                    {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_desc`)}
                  />
                </div>
              </div>
              {isEditMode && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => removeItem(i)}
                  onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                  className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/ncard:opacity-100 transition-opacity cursor-pointer"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </div>
          ))}
          {isEditMode && (
            <button
              onClick={addItem}
              className="border-2 border-dashed border-blue-300 rounded-xl p-6 flex flex-col items-center justify-center text-blue-400 hover:border-zap hover:text-zap hover:bg-zap-pale/50 transition-all min-h-[160px]"
            >
              <Plus className="w-6 h-6 mb-1" />
              <span className="text-xs" style={{ fontWeight: 500 }}>הוסף כרטיס</span>
            </button>
          )}
        </div>
        {dropSlot(`secblk-${block.id}-after-grid`)}
      </div>
    </div>
  );
}

/* ── 4. Feature Cards Section ── */

function SectionFeatureCardsBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const items: { title: string; description: string }[] = block.meta?.items || [];

  const updateItem = (idx: number, field: "title" | "description", val: string) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({
      items: [...items, { title: "תכונה חדשה", description: "תיאור מפורט של התכונה שמסביר ללקוח למה זה חשוב." }],
    });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-white rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-5xl mx-auto">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy text-center mb-10"
          placeholder="כותרת הסקשן..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        {dropSlot(`secblk-${block.id}-after-title`)}
        {/* Cards 2x2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
          {items.map((item, i) => (
            <div key={i} className="relative group/fcard flex items-start gap-4">
              {/* Check icon */}
              <div className="w-10 h-10 rounded-full bg-zap-pale flex items-center justify-center shrink-0 mt-1">
                <Check className="w-5 h-5 text-zap" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <InlineEditable
                  value={item.title}
                  onSave={(val) => updateItem(i, "title", val)}
                  isEditMode={isEditMode}
                  tag="h3"
                  className="text-zap-navy text-base md:text-lg mb-2"
                  placeholder="כותרת..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_title`)}
                />
                <MultilineEditable
                  value={item.description}
                  onSave={(val) => updateItem(i, "description", val)}
                  isEditMode={isEditMode}
                  className="text-gray-600 text-sm leading-relaxed"
                  placeholder="תיאור..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_desc`)}
                />
              </div>
              {isEditMode && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => removeItem(i)}
                  onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                  className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/fcard:opacity-100 transition-opacity cursor-pointer"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </div>
          ))}
        </div>
        {isEditMode && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={addItem}
              className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-blue-300 rounded-lg text-blue-400 hover:border-zap hover:text-zap hover:bg-zap-pale/50 transition-all text-sm"
            >
              <Plus className="w-4 h-4" />
              <span style={{ fontWeight: 500 }}>הוסף תכונה</span>
            </button>
          </div>
        )}
        {dropSlot(`secblk-${block.id}-after-grid`)}
      </div>
    </div>
  );
}

/* ── 5. Service Cards Section ── */

function SectionServiceCardsBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const items: { title: string; description: string }[] = block.meta?.items || [];

  const updateItem = (idx: number, field: "title" | "description", val: string) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({
      items: [...items, { title: "שירות חדש", description: "תיאור קצר של השירות שאתם מציעים ללקוחות." }],
    });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-gray-100 rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy text-center mb-8"
          placeholder="כותרת הסקשן..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        {dropSlot(`secblk-${block.id}-after-title`)}
        {/* Cards row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((item, i) => (
            <div key={i} className="relative group/scard bg-gray-400/80 rounded-2xl p-6 flex flex-col text-white min-h-[200px] hover:bg-gray-500/80 transition-colors">
              {/* Arrow icon */}
              <div className="mb-4">
                <ArrowUpLeft className="w-6 h-6 text-white/80" />
              </div>
              <div className="flex-1">
                <InlineEditable
                  value={item.title}
                  onSave={(val) => updateItem(i, "title", val)}
                  isEditMode={isEditMode}
                  tag="h3"
                  className="text-white text-base md:text-lg mb-3"
                  placeholder="שם השירות..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_title`)}
                />
                <MultilineEditable
                  value={item.description}
                  onSave={(val) => updateItem(i, "description", val)}
                  isEditMode={isEditMode}
                  className="text-white/80 text-xs leading-relaxed"
                  placeholder="תיאור השירות..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_desc`)}
                />
              </div>
              {isEditMode && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => removeItem(i)}
                  onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                  className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/scard:opacity-100 transition-opacity cursor-pointer z-10"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </div>
          ))}
          {isEditMode && (
            <button
              onClick={addItem}
              className="border-2 border-dashed border-blue-300 rounded-2xl p-6 flex flex-col items-center justify-center text-blue-400 hover:border-zap hover:text-zap hover:bg-zap-pale/30 transition-all min-h-[200px]"
            >
              <Plus className="w-6 h-6 mb-1" />
              <span className="text-xs" style={{ fontWeight: 500 }}>הוסף שירות</span>
            </button>
          )}
        </div>
        {dropSlot(`secblk-${block.id}-after-grid`)}
      </div>
    </div>
  );
}

/* ── 6. Testimonials Section ── */

function SectionTestimonialsBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const items: { name: string; role: string; quote: string; rating: number }[] = block.meta?.items || [];

  const updateItem = (idx: number, field: string, val: string | number) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({
      items: [...items, { name: "שם הלקוח", role: "תפקיד, חברה", quote: "המלצה חדשה — לחצו כדי לערוך את הטקסט.", rating: 5 }],
    });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-gray-50 rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy text-center mb-10"
          placeholder="כותרת הסקשן..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        {dropSlot(`secblk-${block.id}-after-title`)}
        {/* Testimonial Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item, i) => (
            <div key={i} className="relative group/tcard bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow flex flex-col">
              {/* Quote icon */}
              <div className="mb-4">
                <QuoteIcon className="w-8 h-8 text-zap/20" />
              </div>
              {/* Stars */}
              <div className="flex gap-0.5 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    role={isEditMode ? "button" : undefined}
                    tabIndex={isEditMode ? 0 : undefined}
                    onClick={isEditMode ? () => updateItem(i, "rating", star) : undefined}
                    onKeyDown={isEditMode ? (e) => e.key === "Enter" && updateItem(i, "rating", star) : undefined}
                    className={isEditMode ? "cursor-pointer" : ""}
                  >
                    <Star
                      className={`w-4 h-4 ${star <= (item.rating || 5) ? "text-amber-400 fill-amber-400" : "text-gray-200"}`}
                    />
                  </span>
                ))}
              </div>
              {/* Quote text */}
              <div className="flex-1 mb-5">
                <MultilineEditable
                  value={item.quote}
                  onSave={(val) => updateItem(i, "quote", val)}
                  isEditMode={isEditMode}
                  className="text-gray-600 text-sm leading-relaxed"
                  placeholder="טקסט ההמלצה..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_quote`)}
                />
              </div>
              {/* Author */}
              <div className="flex items-center gap-3 border-t border-gray-100 pt-4">
                {/* Avatar placeholder */}
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center shrink-0 text-gray-400 text-sm" style={{ fontWeight: 600 }}>
                  {(item.name || "?").charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <InlineEditable
                    value={item.name}
                    onSave={(val) => updateItem(i, "name", val)}
                    isEditMode={isEditMode}
                    tag="p"
                    className="text-zap-navy text-sm"
                    placeholder="שם..."
                    {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_name`)}
                  />
                  <InlineEditable
                    value={item.role}
                    onSave={(val) => updateItem(i, "role", val)}
                    isEditMode={isEditMode}
                    tag="p"
                    className="text-gray-400 text-xs"
                    placeholder="תפקיד..."
                    {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_role`)}
                  />
                </div>
              </div>
              {/* Delete */}
              {isEditMode && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => removeItem(i)}
                  onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                  className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/tcard:opacity-100 transition-opacity cursor-pointer z-10"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </div>
          ))}
          {isEditMode && (
            <button
              onClick={addItem}
              className="border-2 border-dashed border-blue-300 rounded-2xl p-6 flex flex-col items-center justify-center text-blue-400 hover:border-zap hover:text-zap hover:bg-zap-pale/30 transition-all min-h-[260px]"
            >
              <Plus className="w-6 h-6 mb-1" />
              <span className="text-xs" style={{ fontWeight: 500 }}>הוסף המלצה</span>
            </button>
          )}
        </div>
        {dropSlot(`secblk-${block.id}-after-grid`)}
      </div>
    </div>
  );
}

/* ── 7. Split Content Section (Text + Image) ── */

function SectionSplitContentBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const meta = block.meta || {};
  const subtitle = meta.subtitle || "";
  const description = meta.description || "";
  const buttonLabel = meta.buttonLabel || "";
  const buttonLink = meta.buttonLink || "#";
  const imagePosition: "left" | "right" = meta.imagePosition || "left";

  const sectionImageUrl = meta.imageUrl || "";

  const imageBlock = (
    <div className="w-full min-h-[280px] rounded-2xl overflow-hidden">
      <EditableImage
        src={sectionImageUrl}
        onChangeSrc={(url) => onUpdateMeta({ imageUrl: url })}
        isEditMode={isEditMode}
        alt={block.content || ""}
        className="w-full h-64 md:h-full min-h-[280px] object-cover rounded-2xl"
        placeholderClassName="h-64 md:h-full min-h-[280px]"
      />
    </div>
  );

  const textBlock = (
    <div className="flex flex-col justify-center py-4">
      {/* Subtitle / label */}
      <InlineEditable
        value={subtitle}
        onSave={(val) => onUpdateMeta({ subtitle: val })}
        isEditMode={isEditMode}
        tag="p"
        className="text-zap text-sm mb-3"
        placeholder="תת כותרת..."
        {...makeFieldTypo(block.meta, onUpdateMeta, "subtitle")}
      />
      {/* Title */}
      <InlineEditable
        value={block.content}
        onSave={onUpdateContent}
        isEditMode={isEditMode}
        tag="h2"
        className="text-2xl md:text-3xl text-zap-navy mb-5"
        placeholder="כותרת הסקשן..."
        {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
      />
      {/* Description */}
      <MultilineEditable
        value={description}
        onSave={(val) => onUpdateMeta({ description: val })}
        isEditMode={isEditMode}
        className="text-gray-600 text-sm md:text-base leading-relaxed mb-6"
        placeholder="תיאור מפורט..."
        {...makeFieldTypo(block.meta, onUpdateMeta, "desc")}
      />
      {/* Button */}
      {(buttonLabel || isEditMode) && (
        <div className="flex items-center gap-3">
          {isEditMode ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={buttonLabel}
                onChange={(e) => onUpdateMeta({ buttonLabel: e.target.value })}
                className="px-5 py-2.5 bg-zap text-white rounded-lg text-sm outline-none min-w-[100px] text-center"
                style={{ fontWeight: 600 }}
                dir="rtl"
                placeholder="טקסט הכפתור..."
              />
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-gray-400">קישור:</span>
                <input
                  type="text"
                  value={buttonLink}
                  onChange={(e) => onUpdateMeta({ buttonLink: e.target.value })}
                  className="text-[11px] px-2 py-1 border border-gray-200 rounded w-24"
                  dir="ltr"
                />
              </div>
            </div>
          ) : (
            buttonLabel && (
              <a
                href={buttonLink}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-zap text-white rounded-lg text-sm hover:bg-zap-dark transition-colors"
                style={{ fontWeight: 600 }}
              >
                {buttonLabel}
                <ArrowLeft className="w-4 h-4" />
              </a>
            )
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Image position toggle (edit mode) */}
        {isEditMode && (
          <div className="flex justify-end mb-4">
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <span className="text-[11px] text-gray-400 px-2">מיקום תמונה:</span>
              <button
                onClick={() => onUpdateMeta({ imagePosition: "right" })}
                className={`text-[11px] px-3 py-1 rounded-md transition-colors ${
                  imagePosition === "right" ? "bg-zap text-white" : "text-gray-500 hover:bg-gray-200"
                }`}
              >
                ימין
              </button>
              <button
                onClick={() => onUpdateMeta({ imagePosition: "left" })}
                className={`text-[11px] px-3 py-1 rounded-md transition-colors ${
                  imagePosition === "left" ? "bg-zap text-white" : "text-gray-500 hover:bg-gray-200"
                }`}
              >
                שמאל
              </button>
            </div>
          </div>
        )}
        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          {imagePosition === "right" ? (
            <>
              {textBlock}
              {imageBlock}
            </>
          ) : (
            <>
              {imageBlock}
              {textBlock}
            </>
          )}
        </div>
        {dropSlot(`secblk-${block.id}-after-grid`)}
      </div>
    </div>
  );
}

/* ─ 8. Logos / Partners Section ── */

function SectionLogosBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const meta = block.meta || {};
  const subtitle = meta.subtitle || "";
  const items: { name: string; imageUrl?: string }[] = meta.items || [];

  const updateItem = (idx: number, field: string, val: string) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({ items: [...items, { name: "חברה חדשה", imageUrl: "" }] });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-white rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-5xl mx-auto text-center">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy mb-2"
          placeholder="כותרת הסקשן..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        {/* Subtitle */}
        <InlineEditable
          value={subtitle}
          onSave={(val) => onUpdateMeta({ subtitle: val })}
          isEditMode={isEditMode}
          tag="p"
          className="text-gray-500 text-sm mb-10"
          placeholder="תת כותרת..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "subtitle")}
        />
        {dropSlot(`secblk-${block.id}-after-subtitle`)}
        {/* Logo grid */}
        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
          {items.map((item, i) => (
            <div key={i} className="relative group/logo">
              <div className="w-28 h-16 md:w-36 md:h-20 rounded-xl overflow-hidden border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all">
                {item.imageUrl ? (
                  <EditableImage
                    src={item.imageUrl}
                    onChangeSrc={(url) => updateItem(i, "imageUrl", url)}
                    isEditMode={isEditMode}
                    alt={item.name}
                    className="w-full h-full object-contain p-2"
                    placeholderClassName="w-full h-full"
                  />
                ) : isEditMode ? (
                  <EditableImage
                    src=""
                    onChangeSrc={(url) => updateItem(i, "imageUrl", url)}
                    isEditMode={isEditMode}
                    alt={item.name}
                    className="w-full h-full object-contain p-2"
                    placeholderClassName="w-full h-full"
                    placeholderText={item.name}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <span className="text-xs text-gray-400" style={{ fontWeight: 500 }}>{item.name}</span>
                  </div>
                )}
              </div>
              {isEditMode && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => removeItem(i)}
                  onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                  className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/logo:opacity-100 transition-opacity cursor-pointer z-10"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </div>
          ))}
          {isEditMode && (
            <button
              onClick={addItem}
              className="w-28 h-16 md:w-36 md:h-20 border-2 border-dashed border-blue-200 rounded-xl flex flex-col items-center justify-center text-blue-400 hover:border-zap hover:text-zap hover:bg-zap-pale/30 transition-all"
            >
              <Plus className="w-5 h-5 mb-0.5" />
              <span className="text-[10px]" style={{ fontWeight: 500 }}>הוסף לוגו</span>
            </button>
          )}
        </div>
        {dropSlot(`secblk-${block.id}-after-grid`)}
      </div>
    </div>
  );
}

/* ── 9. Blog Posts Section ── */

function SectionBlogPostsBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const meta = block.meta || {};
  const subtitle = meta.subtitle || "";
  const items: { title: string; excerpt: string; date: string; imageUrl?: string }[] = meta.items || [];
  const buttonLabel = meta.buttonLabel || "";
  const buttonLink = meta.buttonLink || "#";

  const updateItem = (idx: number, field: "title" | "excerpt" | "date", val: string) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({
      items: [...items, { title: "פוסט חדש", excerpt: "תיאור קצר של הפוסט. לחצו כאן כדי לערוך.", date: "1 במרץ 2026", imageUrl: "" }],
    });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-white rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy text-center mb-10"
          placeholder="כותרת הסקשן..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        {/* Subtitle */}
        <InlineEditable
          value={subtitle}
          onSave={(val) => onUpdateMeta({ subtitle: val })}
          isEditMode={isEditMode}
          tag="p"
          className="text-gray-500 text-sm mb-10"
          placeholder="תת כותרת..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "subtitle")}
        />
        {dropSlot(`secblk-${block.id}-after-subtitle`)}
        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item, i) => (
            <div key={i} className="relative group/bpost bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow flex flex-col">
              {/* Blog post image */}
              <EditableImage
                src={item.imageUrl}
                onChangeSrc={(url) => {
                  const next = items.map((it, idx) => (idx === i ? { ...it, imageUrl: url } : it));
                  onUpdateMeta({ items: next });
                }}
                isEditMode={isEditMode}
                alt={item.title}
                className="w-full h-40 object-cover"
                placeholderClassName="h-40"
              />
              <div className="p-6 flex flex-col flex-1">
                {/* Title */}
                <InlineEditable
                  value={item.title}
                  onSave={(val) => updateItem(i, "title", val)}
                  isEditMode={isEditMode}
                  tag="h3"
                  className="text-zap-navy text-base md:text-lg mb-3"
                  placeholder="כותרת הפוסט..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_title`)}
                />
                {/* Excerpt */}
                <MultilineEditable
                  value={item.excerpt}
                  onSave={(val) => updateItem(i, "excerpt", val)}
                  isEditMode={isEditMode}
                  className="text-gray-600 text-sm leading-relaxed mb-5"
                  placeholder="תקציר הפוסט..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_excerpt`)}
                />
                {/* Date */}
                <InlineEditable
                  value={item.date}
                  onSave={(val) => updateItem(i, "date", val)}
                  isEditMode={isEditMode}
                  tag="p"
                  className="text-gray-400 text-xs"
                  placeholder="תאריך הפוסט..."
                  {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_date`)}
                />
              </div>
              {/* Delete */}
              {isEditMode && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => removeItem(i)}
                  onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                  className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/bpost:opacity-100 transition-opacity cursor-pointer z-10"
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </div>
          ))}
          {isEditMode && (
            <button
              onClick={addItem}
              className="border-2 border-dashed border-blue-300 rounded-2xl p-6 flex flex-col items-center justify-center text-blue-400 hover:border-zap hover:text-zap hover:bg-zap-pale/30 transition-all min-h-[260px]"
            >
              <Plus className="w-6 h-6 mb-1" />
              <span className="text-xs" style={{ fontWeight: 500 }}>הוסף פוסט</span>
            </button>
          )}
        </div>
        {dropSlot(`secblk-${block.id}-after-grid`)}
        {/* Button */}
        {(buttonLabel || isEditMode) && (
          <div className="mt-6 flex justify-center">
            {isEditMode ? (
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  value={buttonLabel}
                  onChange={(e) => onUpdateMeta({ buttonLabel: e.target.value })}
                  className="px-5 py-2.5 bg-zap text-white rounded-lg text-sm outline-none min-w-[100px] text-center"
                  style={{ fontWeight: 600 }}
                  dir="rtl"
                  placeholder="טקסט הכפתור..."
                />
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-gray-400">קישור:</span>
                  <input
                    type="text"
                    value={buttonLink}
                    onChange={(e) => onUpdateMeta({ buttonLink: e.target.value })}
                    className="text-[11px] px-2 py-1 border border-gray-200 rounded w-24"
                    dir="ltr"
                  />
                </div>
              </div>
            ) : (
              buttonLabel && (
                <a
                  href={buttonLink}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-zap text-white rounded-lg text-sm hover:bg-zap-dark transition-colors"
                  style={{ fontWeight: 600 }}
                >
                  {buttonLabel}
                  <ArrowLeft className="w-4 h-4" />
                </a>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── 10. Process Steps Section ── */

function SectionProcessStepsBlock({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const dropSlot = useSectionDropSlot();
  const meta = block.meta || {};
  const subtitle = meta.subtitle || "";
  const items: { title: string; description: string }[] = meta.items || [];

  const updateItem = (idx: number, field: "title" | "description", val: string) => {
    const next = items.map((item, i) => (i === idx ? { ...item, [field]: val } : item));
    onUpdateMeta({ items: next });
  };

  const addItem = () => {
    onUpdateMeta({
      items: [...items, { title: "\u05E6\u05E2\u05D3 \u05D7\u05D3\u05E9", description: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 \u05E9\u05DC \u05D4\u05E6\u05E2\u05D3 \u05D4\u05D1\u05D0 \u05D1\u05E2\u05D1\u05D5\u05D3\u05EA\u05E0\u05D5." }],
    });
  };

  const removeItem = (idx: number) => {
    onUpdateMeta({ items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white rounded-2xl p-8 md:p-12" dir="rtl">
      <div className="max-w-5xl mx-auto">
        {/* Title */}
        <InlineEditable
          value={block.content}
          onSave={onUpdateContent}
          isEditMode={isEditMode}
          tag="h2"
          className="text-2xl md:text-3xl text-zap-navy text-center mb-3"
          placeholder="\u05DB\u05D5\u05EA\u05E8\u05EA \u05D4\u05E1\u05E7\u05E9\u05DF..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "title")}
        />
        {/* Subtitle */}
        <InlineEditable
          value={subtitle}
          onSave={(val) => onUpdateMeta({ subtitle: val })}
          isEditMode={isEditMode}
          tag="p"
          className="text-gray-500 text-sm text-center mb-12"
          placeholder="\u05EA\u05EA \u05DB\u05D5\u05EA\u05E8\u05EA..."
          {...makeFieldTypo(block.meta, onUpdateMeta, "subtitle")}
        />
        {dropSlot(`secblk-${block.id}-after-subtitle`)}

        {/* Steps with timeline */}
        <div className="relative">
          {/* Connector line \u2014 desktop horizontal */}
          {items.length > 1 && (
            <div
              className="hidden md:block absolute top-8 right-8 left-8 h-0.5 bg-gradient-to-l from-zap/20 via-zap/40 to-zap/20"
              style={{ zIndex: 0 }}
            />
          )}
          {/* Connector line \u2014 mobile vertical */}
          {items.length > 1 && (
            <div
              className="md:hidden absolute top-8 right-8 bottom-8 w-0.5 bg-gradient-to-b from-zap/20 via-zap/40 to-zap/20"
              style={{ zIndex: 0 }}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6 relative" style={{ zIndex: 1 }}>
            {items.map((item, i) => (
              <div key={i} className="relative group/step flex flex-col items-center text-center">
                {/* Step number circle */}
                <div className="w-16 h-16 rounded-full bg-white border-2 border-zap flex items-center justify-center mb-5 shadow-md">
                  <span className="text-xl text-zap" style={{ fontWeight: 800 }}>{i + 1}</span>
                </div>
                {/* Card body */}
                <div className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow w-full">
                  <InlineEditable
                    value={item.title}
                    onSave={(val) => updateItem(i, "title", val)}
                    isEditMode={isEditMode}
                    tag="h3"
                    className="text-zap-navy text-base mb-2"
                    placeholder="\u05DB\u05D5\u05EA\u05E8\u05EA \u05D4\u05E6\u05E2\u05D3..."
                    {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_title`)}
                  />
                  <MultilineEditable
                    value={item.description}
                    onSave={(val) => updateItem(i, "description", val)}
                    isEditMode={isEditMode}
                    className="text-gray-500 text-sm leading-relaxed"
                    placeholder="\u05EA\u05D9\u05D0\u05D5\u05E8 \u05D4\u05E6\u05E2\u05D3..."
                    {...makeFieldTypo(block.meta, onUpdateMeta, `item_${i}_desc`)}
                  />
                </div>
                {/* Delete button */}
                {isEditMode && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => removeItem(i)}
                    onKeyDown={(e) => e.key === "Enter" && removeItem(i)}
                    className="absolute top-0 left-0 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/step:opacity-100 transition-opacity cursor-pointer z-10"
                  >
                    <X className="w-3 h-3" />
                  </span>
                )}
              </div>
            ))}
            {isEditMode && (
              <div className="flex flex-col items-center justify-center">
                <button
                  type="button"
                  onClick={addItem}
                  className="w-16 h-16 rounded-full border-2 border-dashed border-blue-300 flex items-center justify-center text-blue-400 hover:border-zap hover:text-zap hover:bg-zap-pale/30 transition-all mb-5"
                >
                  <Plus className="w-6 h-6" />
                </button>
                <span className="text-xs text-blue-400" style={{ fontWeight: 500 }}>{"\u05D4\u05D5\u05E1\u05E3 \u05E9\u05DC\u05D1"}</span>
              </div>
            )}
          </div>
        </div>
        {dropSlot(`secblk-${block.id}-after-grid`)}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN SECTION RENDERER — delegates to correct component
   ═══════════════════════════════════════════════ */

export function SectionBlockRenderer({
  block,
  isEditMode,
  onUpdateContent,
  onUpdateMeta,
}: {
  block: ContentBlock;
  isEditMode: boolean;
  onUpdateContent: (c: string) => void;
  onUpdateMeta: (m: Record<string, any>) => void;
}) {
  const props = { block, isEditMode, onUpdateContent, onUpdateMeta };

  if (isStoreSectionType(block.type)) {
    return <StoreSectionBlockRenderer {...props} />;
  }

  switch (block.type) {
    case "sectionContactForm":
      return <SectionContactFormBlock {...props} />;
    case "sectionBenefits":
      return <SectionBenefitsBlock {...props} />;
    case "sectionNumberedCards":
      return <SectionNumberedCardsBlock {...props} />;
    case "sectionFeatureCards":
      return <SectionFeatureCardsBlock {...props} />;
    case "sectionServiceCards":
      return <SectionServiceCardsBlock {...props} />;
    case "sectionTestimonials":
      return <SectionTestimonialsBlock {...props} />;
    case "sectionSplitContent":
      return <SectionSplitContentBlock {...props} />;
    case "sectionLogos":
      return <SectionLogosBlock {...props} />;
    case "sectionBlogPosts":
      return <SectionBlogPostsBlock {...props} />;
    case "sectionProcessSteps":
      return <SectionProcessStepsBlock {...props} />;
    default:
      return null;
  }
}

/** Check if a block type is a section type */
export function isSectionType(type: string): boolean {
  return type.startsWith("section");
}